export const MINIMUM_AI_LOADING_MS = 10_000
export const AI_STAGE_COMPLETION_OFFSETS_MS = [2200, 4600, 7000, 9400] as const

const AI_RESULT_REVEAL_MS = Math.max(
  MINIMUM_AI_LOADING_MS,
  AI_STAGE_COMPLETION_OFFSETS_MS.at(-1) ?? 0
)

export type AILoadingScope = 'recommendation' | 'yield' | 'price' | 'disease' | 'calendar'

function storageKey(scope: AILoadingScope) {
  return `${scope}_loading_started_at`
}

export function markAILoadingStarted(scope: AILoadingScope) {
  const startedAt = Date.now()
  if (typeof window === 'undefined') return startedAt

  try {
    sessionStorage.setItem(storageKey(scope), String(startedAt))
  } catch {
    // Storage access can be unavailable in privacy-restricted browser contexts.
  }
  return startedAt
}

export function readAILoadingStartedAt(scope: AILoadingScope) {
  if (typeof window === 'undefined') return Date.now()

  try {
    const now = Date.now()
    const value = Number(sessionStorage.getItem(storageKey(scope)))
    const isRecentStart =
      Number.isFinite(value) && value > 0 && value <= now && now - value < 60_000

    if (isRecentStart) return value

    sessionStorage.setItem(storageKey(scope), String(now))
    return now
  } catch {
    return Date.now()
  }
}

export function clearAILoadingStartedAt(scope: AILoadingScope) {
  if (typeof window === 'undefined') return

  try {
    sessionStorage.removeItem(storageKey(scope))
  } catch {
    // Storage access can be unavailable in privacy-restricted browser contexts.
  }
}

export function getRemainingAILoadingMs(startedAt: number, now = Date.now()) {
  return Math.max(0, AI_RESULT_REVEAL_MS - (now - startedAt))
}

export function hasAILoadingExceededMinimum(startedAt: number, now = Date.now()) {
  return now - startedAt > MINIMUM_AI_LOADING_MS
}

export function getCompletedAIStageCount(startedAt: number, now = Date.now()) {
  const elapsedMs = Math.max(0, now - startedAt)
  return AI_STAGE_COMPLETION_OFFSETS_MS.filter((offset) => elapsedMs >= offset).length
}
